import { Head, Link, useForm, usePage } from "@inertiajs/react"
import { AppLayout } from "@/layouts/app-layout"
import { PageHeader } from "@/hooks/use-page-header"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"

import type { Team, SharedProps } from "@/types"

type Props = {
  team: Team
  organisations: { id: number; name: string }[]
  teams: { id: number; name: string; organisation_id: number }[]
}

function TeamEdit({ team, organisations, teams }: Props) {
  const { current_user } = usePage<SharedProps>().props

  const { data, setData, patch, processing } = useForm({
    name: team.name,
    organisation_id: String(team.organisation_id),
    parent_team_id: team.parent_team_id ? String(team.parent_team_id) : "",
  })

  const filteredTeams = teams.filter((t) => t.organisation_id === Number(data.organisation_id))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    patch(`/teams/${team.id}`)
  }

  return (
    <>
      <Head title={`Modifier ${team.name}`} />
      <PageHeader title={`Modifier ${team.name}`} back="/teams" />
      <div className="space-y-6">
        <form onSubmit={handleSubmit}>
          <Card>
            <CardHeader>
              <CardTitle>Informations</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nom</Label>
                <Input
                  id="name"
                  value={data.name}
                  onChange={(e) => setData("name", e.target.value)}
                  required
                  maxLength={120}
                />
              </div>

              {current_user?.is_super_admin && (
                <div className="space-y-2">
                  <Label htmlFor="organisation">Organisation</Label>
                  <Select
                    value={data.organisation_id}
                    onValueChange={(v) => {
                      setData("organisation_id", v)
                      setData("parent_team_id", "")
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {organisations.map((org) => (
                        <SelectItem key={org.id} value={String(org.id)}>
                          {org.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {filteredTeams.length > 0 && (
                <div className="space-y-2">
                  <Label htmlFor="parent_team">Équipe parente (optionnel)</Label>
                  <Select value={data.parent_team_id} onValueChange={(v) => setData("parent_team_id", v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Aucune" />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredTeams.map((t) => (
                        <SelectItem key={t.id} value={String(t.id)}>
                          {t.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" asChild>
                  <Link href="/teams">Annuler</Link>
                </Button>
                <Button type="submit" disabled={processing}>
                  Enregistrer
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>
      </div>
    </>
  )
}

TeamEdit.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>

export default TeamEdit
